// Thin compatibility layer between the former redux-form API and
// react-final-form. redux-form does not support React 19, so forms are
// rendered by react-final-form while keeping redux-form semantics:
//   * `validate` on a Field may be an array (first error wins);
//   * `normalize` maps to `parse`, and empty strings are kept as '' (final-form
//     would drop them, which changes PATCH payloads);
//   * initialValues are read once per mount (redux-form without
//     enableReinitialize);
//   * `submitFailed` is cleared after the next change when
//     `resetSubmitFailedOnChange` is set (former `stopSubmit` in onChange).
import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Field as FinalField, Form } from 'react-final-form';
import arrayMutators from 'final-form-arrays';


const formRegistry = new Map();

export const resetForm = (name) => {
  const form = formRegistry.get(name);
  if (form) {
    form.reset();
  }
};

export const composeValidators = (validate) => {
  if (!validate) {
    return undefined;
  }
  if (!Array.isArray(validate)) {
    return validate;
  }
  const validators = validate.filter(Boolean);
  return (value, allValues, meta) => validators.reduce(
    (error, validator) => error || validator(value, allValues, meta),
    undefined,
  );
};

const keepValue = value => value;

const withCallback = (handler, callback) => {
  if (!callback) {
    return handler;
  }
  return (event) => {
    callback(event);
    if (!(event && event.defaultPrevented)) {
      handler(event);
    }
  };
};

export const Field = ({
  component: Component,
  validate,
  normalize,
  parse,
  onChange,
  onFocus,
  onBlur,
  ...rest
}) => (
  <FinalField
    {...rest}
    parse={normalize || parse || keepValue}
    validate={composeValidators(validate)}
  >
    {({ input, meta, ...props }) => (
      <Component
        {...props}
        input={{
          ...input,
          onChange: withCallback(input.onChange, onChange),
          onFocus: withCallback(input.onFocus, onFocus),
          onBlur: withCallback(input.onBlur, onBlur),
        }}
        meta={meta}
      />
    )}
  </FinalField>
);

Field.propTypes = {
  name: PropTypes.string.isRequired,
  component: PropTypes.elementType.isRequired,
};

const FormRegistration = ({ name, form }) => {
  useEffect(() => {
    formRegistry.set(name, form);
    return () => {
      if (formRegistry.get(name) === form) {
        formRegistry.delete(name);
      }
    };
  }, [name, form]);
  return null;
};

const mutators = { ...arrayMutators };

export const withForm = ({
  form: formName,
  validate,
  resetSubmitFailedOnChange = false,
}) => (Component) => {
  const WithForm = ({ onSubmit, initialValues, ...props }) => {
    const [initial] = useState(initialValues);
    return (
      <Form
        onSubmit={onSubmit}
        initialValues={initial}
        validate={validate}
        mutators={mutators}
      >
        {({
          handleSubmit,
          form,
          submitting,
          pristine,
          dirty,
          submitSucceeded,
          submitFailed,
          modifiedSinceLastSubmit,
        }) => (
          <>
            <FormRegistration name={formName} form={form} />
            <Component
              {...props}
              initialValues={initial}
              handleSubmit={handleSubmit}
              submitting={submitting}
              pristine={pristine}
              dirty={dirty}
              submitSucceeded={submitSucceeded}
              submitFailed={resetSubmitFailedOnChange
                ? submitFailed && !modifiedSinceLastSubmit
                : submitFailed}
              change={form.change}
              reset={form.reset}
              form={form}
            />
          </>
        )}
      </Form>
    );
  };
  WithForm.displayName = `withForm(${formName})`;
  WithForm.propTypes = {
    onSubmit: PropTypes.func.isRequired,
  };
  return WithForm;
};
